import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Form } from '@hologram-dimension/form';
import { Modal } from '@hologram-dimension/modal';
import { TextInputField } from '@hologram-dimension/text-input-field';
import useGenerateRouterCredentials from 'api/legacy/devices/useGenerateRouterCredentials';
import useRouterCredentials from 'api/legacy/devices/useRouterCredentials';
import type { CommonModalProps } from 'modals/shared';
import { useCallback, useMemo, useState, type FC } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';

interface RouterCredentialsModalProps extends CommonModalProps {
  deviceId?: DeviceId;
}

const RouterCredentialsModal: FC<RouterCredentialsModalProps> = ({
  deviceId,
  onClose,
  defaultOpen,
}) => {
  const organizationId = useOrganization();
  const generateRouterCredentials = useGenerateRouterCredentials({
    organizationId,
    deviceId,
  });

  const {
    allRouterCredentials,
    isLoading: isLoadingRouterCredentials,
    isValidating: isValidatingRouterCredentials,
    mutate: mutateRouterCredentials,
    error: errorLoadingExistingRouterCredentials,
  } = useRouterCredentials({
    organizationId,
    deviceId,
  });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const routerCredentials = useMemo(
    () => (allRouterCredentials.length > 0 ? allRouterCredentials[0] : undefined),
    [allRouterCredentials]
  );

  const onCopyToClipboard = useCallback(() => {
    if (!routerCredentials?.devicekey) {
      setError('No device key to copy.');
      return;
    }
    setError(null);
    navigator.clipboard.writeText(routerCredentials.devicekey);
    setCopiedToClipboard(true);
  }, [routerCredentials?.devicekey]);

  const onGenerateRouterCreds = useCallback(async () => {
    setProcessing(true);
    setError(null);

    let result;

    try {
      result = await generateRouterCredentials();
    } catch (generateError) {
      setError(generateError as string);
    }

    if (result?.success && result?.data) {
      setCopiedToClipboard(false);
      mutateRouterCredentials(
        { data: [result.data, ...structuredClone(allRouterCredentials)], success: true },
        { revalidate: true }
      );
    }
    setProcessing(false);
  }, [allRouterCredentials, generateRouterCredentials, mutateRouterCredentials]);

  return (
    <Modal
      header="Router credentials"
      footerActions={
        <>
          {routerCredentials?.devicekey && (
            <Button
              onClick={onCopyToClipboard}
              variant="secondary"
              iconStart={copiedToClipboard ? 'CheckmarkSingle' : 'Document'}
            >
              Copy key
            </Button>
          )}
          <Button
            onClick={onGenerateRouterCreds}
            loading={processing || isValidatingRouterCredentials}
            disabled={isLoadingRouterCredentials || !!errorLoadingExistingRouterCredentials}
          >
            Generate new device key
          </Button>
        </>
      }
      onClosed={onClose}
      defaultOpen={defaultOpen}
    >
      {error ||
        (errorLoadingExistingRouterCredentials && (
          <BannerNotification variant="system-error">
            {error || (errorLoadingExistingRouterCredentials as any)}
          </BannerNotification>
        ))}
      <Form>
        <TextInputField
          inputProps={{ value: routerCredentials?.devicekey ?? 'Not generated yet' } as any}
          readOnly
          label="Device key"
          layoutDirection="row"
        />
      </Form>
    </Modal>
  );
};

export default RouterCredentialsModal;
